import {
  GoogleForJobsResponse,
  JobDetailResponse,
  JobRecruitResponse,
  JobResponse,
  JobRecruitSalaryExampleResponse,
  JobInterviewEmployeeResponse,
} from '@doda/api-client/aspida/@types';
import { WJOFL_DIRECT } from '@doda/common/src/constants/properties/const';
import { COMMA_N } from '@doda/common/src/constants/seo';
// dateをiso型に変換する処理
const formatDate = (date: Date) => {
  const y = date.getFullYear();
  const m = `00${date.getMonth() + 1}`.slice(-2);
  const d = `00${date.getDate()}`.slice(-2);
  return `${y}-${m}-${d}`;
};

/**
 * 日付の再生成
 * @param {string | null} date
 * @returns {string | null}
 */
export const reWriteDate = (date: string | null) => {
  if (!date) return null;
  const fixedDate = date.replace(/（\D）/, '');
  const dateObj = new Date(fixedDate);
  const isoDate = formatDate(dateObj);
  return isoDate;
};

/**
 * 契約タイプを構造化データ規定の形に変換する処理
 * @param employmentType
 * @returns {("FULL_TIME" | "CONTRACTOR" | "TEMPORARY" | "OTHER")[] | null}
 */
export const replaceEmploymentType = (employmentType: string | null) => {
  if (!employmentType) return null;
  // 参考:: https://intebi0001.backlog.jp/view/DODA_TOP_REBUILD-1789#comment-1343071905

  // 契約タイプが複数選択された場合「派遣社員／紹介予定派遣」のように値が返ってくるため、配列に直す
  const fixedEmploymentTypeData = employmentType.split('／');

  const replacedData = fixedEmploymentTypeData.map((data) => {
    switch (data) {
      case '正社員':
        return 'FULL_TIME';
      case '契約社員':
        return 'CONTRACTOR';
      case '紹介予定派遣':
      case '派遣社員':
        return 'TEMPORARY';
      case '業務委託':
      case 'FCオーナー':
      case 'その他':
        return 'OTHER';
      default:
        return 'OTHER';
    }
  });

  // 契約タイプの選択が１つだった時は文字列で返す
  if (replacedData.length === 1) {
    return replacedData.toString();
  }

  return replacedData;
};

/**
 * 企業名を生成する処理
 * @param corporateName
 * @param wcnsId
 * @returns {string | null}
 */
export const createCompanyName = (
  corporateName: string | null,
  wcnsId: string | null
) => {
  if (wcnsId === 'T') {
    // T原稿で、企業名に該当記号が含まれる場合、記号を含む文字列を削除する
    // A～E原稿は修正不要、A～E原稿は、企業名と上場先の入力フォームが分かれており、不要な表現が入る恐れがないため。
    // 参考: https://product-scrum.persol-career.co.jp/issues/91634
    return corporateName?.replaceAll(
      /【.*?】|<.*?>|＜.*?＞|^\\(.*?\\)|\\(.*\\)$|^（.*?）|（.*）$|~.*|～.*|※.*/g,
      ''
    );
  }
  return corporateName;
};

/**
 * 住所の配列データを作成する処理
 * @param jobAddress
 * @param prefectureNameList
 * @param areaId
 * @returns {Array}
 */
export const createAdressArray = (
  jobAddress: JobDetailResponse['locations'],
  prefectureNameList: string[] | undefined, // jobAddressが空のとき代わりに使用
  areaId: string | null | undefined
) => {
  const newJobAddress = jobAddress?.length
    ? jobAddress.map((data) => {
        return {
          '@type': 'PostalAddress',
          addressCountry: areaId !== '99' ? 'JP' : null, // 選択された勤務地が日本国内 → JP / 選択された勤務地が海外 → 出力しない
          addressLocality: data.name,
          addressRegion: data.prefectureName,
        };
      })
    : prefectureNameList
        ?.filter((item) => item !== '海外')
        ?.map((prefectureName) => {
          return {
            '@type': 'PostalAddress',
            addressCountry: areaId !== '99' ? 'JP' : null, // 選択された勤務地が日本国内 → JP / 選択された勤務地が海外 → 出力しない
            addressLocality: undefined,
            addressRegion: prefectureName,
          };
        });

  return newJobAddress;
};

/**
 * タイトルを生成する処理(フラグがtrueの職種分類名を"-"区切りで設定する)
 * @param googleForJobsResponse
 * @param jobType
 * @returns {string}
 */
export const createTitle = (
  googleForJobsResponse: GoogleForJobsResponse,
  jobType: JobDetailResponse['searchJobType']
) => {
  const { isJobTypeLDisplay, isJobTypeMDisplay, isJobTypeSDisplay } =
    googleForJobsResponse;

  const jobTypes = {
    jobTypeL: isJobTypeLDisplay ? jobType?.searchJobTypeLName : null,
    jobTypeM: isJobTypeMDisplay ? jobType?.searchJobTypeMName : null,
    jobTypeS: isJobTypeSDisplay ? jobType?.searchJobTypeSName : null,
  };

  const jobTypeKeys = Object.keys(jobTypes);

  const title = jobTypeKeys
    .map((key, ind) => {
      const value = jobTypes[key as keyof typeof jobTypes];
      if (!value) return null;

      const preValue = jobTypes[jobTypeKeys[ind - 1] as keyof typeof jobTypes];

      // 1つ前に値がある場合は'-'で繋ぐ
      if (preValue) {
        return `-${value}`;
      }
      if (ind === 2) {
        // jobTypeMのみfalseの場合
        return jobTypes.jobTypeL ? `-${value}` : value;
      }
      return value;
    })
    .join('');

  return title;
};

// ↓↓ descriotionを生成する処理 ↓↓
const createDescriptionTitle = (text: string | undefined): string => {
  return `<p>-------------------------------------------<br>${text}<br>-------------------------------------------</p>`;
};

const createDescriptionText = (
  text: string | (string | undefined)[]
): string => {
  let content = '';

  if (typeof text === 'string') {
    content = `<p>${text}</p>`;
  } else if (typeof text === 'object') {
    content = text
      .map((textData) => {
        if (!textData) return null; // 任意項目の場合はエスケープ
        return `<p>${textData}</p>`;
      })
      .join('');
  }

  return content;
};

/**
 * DescriptionのリストテキストDOMの生成
 * @param textArray
 * @returns {string}
 */
export const createDescriptionListText = (textArray: string[]): string => {
  let content = '';

  if (textArray.length > 0) {
    content += `<ul>`;
    content += textArray
      .map((textData) => {
        if (!textData) return null; // 任意項目の場合はエスケープ
        return `<li>${textData}</li>`;
      })
      .join('');
    content += `</ul>`;
  }
  return content;
};

/**
 * Descriptionの給与情報DOMの生成
 * @param salaryList
 * @returns {string}
 */
export const createDescriptionSalary = (
  salaryList: JobRecruitSalaryExampleResponse[]
): string => {
  let content = '';
  if (salaryList.length > 0) {
    content += `<ul>`;
    content += salaryList
      .map((textData) => {
        if (!textData?.text) return null;
        return `<li>${textData.text}</li>`;
      })
      .join('');
    content += `</ul>`;
  }
  return content;
};

/**
 * Descriptionのインタビュー情報DOMの生成
 * @param interviewList
 * @returns {string}
 */
export const createDescriptionInterview = (
  interviewList: JobInterviewEmployeeResponse[]
): string => {
  let content = '';
  if (interviewList.length > 0) {
    content += interviewList
      .map((interview) => {
        let text = '';
        text += '<p>■';
        if (interview?.relationship) text += `${interview.relationship}／`;
        if (interview?.name) text += `${interview.name}／`;
        if (interview?.entity) text += `${interview.entity}／`;
        if (interview?.relationship || interview?.name || interview?.entity)
          text = text.slice(0, text.length - 1);
        text += '</p>';
        if (interview?.text) text += `<p>${interview.text}</p>`;
        return text;
      })
      .join('');
  }
  return content;
};

// 配列データの中で、値が存在しているか否かを判定する。1つでも値が入っていれば構造化データの項目として書き出す。
const judgeHavingData = (array: unknown[]) => {
  const result = array.some((data) => {
    return !!data;
  });

  return result;
};

/**
 * DescriptionのDOMを生成
 * @param jobSearchDetail
 * @returns {string}
 */
export const createDescription = (jobSearchDetail: JobDetailResponse) => {
  const { job } = jobSearchDetail;

  if (!job) return null;

  let description = '';

  /**
   * 求人要約
   * 仕事内容
   * チーム組織構成
   * 組織名称
   * 職種別項目１
   * 職種別項目２
   * 対象となる方
   * 年齢制限理由
   * 選考のポイント
   * 勤務地
   * 勤務時間
   * 平均残業時間
   * 雇用形態
   * 給与
   * 給与にプラスしてもらえる手当・インセンティブ
   * 賞与
   * 昇給
   * 入社時の想定年収
   * 待遇・福利厚生
   * 休日・休暇
   */

  // 求人要約
  if (job.articleSummaryList && job.articleSummaryList.length > 0) {
    const jobSummary = createDescriptionTitle('求人要約');
    description +=
      jobSummary + createDescriptionListText(job.articleSummaryList);
  }
  // 本文 or 一覧用メッセージ
  if (
    job.companyMessage &&
    job.wcnsId &&
    (job.wcnsId === 'A' || job.wcnsId === 'E')
  ) {
    const contentData = [job.companyMessage.listExclusiveMessage];
    const text = createDescriptionText(contentData);
    description += text;
  } else if (
    job.companyInfo &&
    job.wcnsId &&
    (job.wcnsId === 'B' || job.wcnsId === 'C' || job.wcnsId === 'D')
  ) {
    const contentData = [job.companyInfo?.companyOverviewText];
    const text = createDescriptionText(contentData);
    description += text;
  }

  // 仕事内容 ※jobContentDetailはT原稿のみ任意項目
  if (job.recruit?.jobContentOutline) {
    const title = createDescriptionTitle('仕事内容');
    const contentData = [
      job.recruit.jobContentOutline,
      job.recruit.jobContentDetail,
    ];
    const text = createDescriptionText(contentData);
    description = description + title + text;
  }

  // チーム組織構成 ※T原稿のみ任意項目
  if (job.recruit?.teamStructure) {
    const title = createDescriptionTitle('チーム組織構成');
    const contentData = [job.recruit.teamStructure];
    const text = createDescriptionText(contentData);
    description = description + title + text;
  }

  // 組織名称 ※任意項目
  const organizationData = {
    genderRatio: '男女比',
    ageComposition: '年齢構成',
    retentionRate: '定着率',
  } as const;

  const isHavingOrganizationData = Object.keys(organizationData).some(
    (key) => job.recruit?.[key as keyof typeof organizationData]
  );

  if (job.recruit?.organizationCategory || isHavingOrganizationData) {
    const title = createDescriptionTitle('組織名称');
    const organizationCategoryText = job.recruit?.organizationCategory
      ? `<p>${job.recruit?.organizationCategory}</p>`
      : '';

    const organizationInfoDetail = Object.keys(organizationData)
      .map((key) => {
        return job.recruit?.[key as keyof typeof organizationData]
          ? // eslint-disable-next-line no-irregular-whitespace
            `・${organizationData[key as keyof typeof organizationData]}　→　${job.recruit?.[key as keyof typeof organizationData]}`
          : null;
      })
      .filter((v): v is string => typeof v === 'string');

    const organizationInfoDetailText =
      organizationInfoDetail.length > 0
        ? `<p>${organizationInfoDetail.join('<br>')}</p>`
        : '';

    description =
      description +
      title +
      organizationCategoryText +
      organizationInfoDetailText;
  }

  // 職種別項目１(その他プロジェクト事例) ※任意項目
  if (job.recruit?.projectCase) {
    const title = createDescriptionTitle(job.recruit.projectCaseTitle);
    const text = createDescriptionText(job.recruit.projectCase);
    description = description + title + text;
  }

  // 職種別項目２(利用するツール・ソフト等) ※任意項目
  if (job.recruit?.developmentEnvironment) {
    const title = createDescriptionTitle(
      job.recruit.developmentEnvironmentTitle
    );
    const text = createDescriptionText(job.recruit.developmentEnvironment);
    description = description + title + text;
  }

  // 対象となる方 ※targetMemberDetailはT原稿のみ任意項目
  if (job.recruit?.targetMemberOutline) {
    const title = createDescriptionTitle('対象となる方');
    const contentData = [
      job.recruit.targetMemberOutline,
      job.recruit.targetMemberDetail,
    ];
    const text = createDescriptionText(contentData);
    description = description + title + text;
  }

  // 年齢制限理由 ※indicatorReasonはT原稿のみ任意項目で入力可能
  if (job.recruit?.ageLimitDisplay) {
    const title = createDescriptionTitle('年齢制限理由');
    const contentData = [
      job.recruit.ageLimitDisplay,
      job.recruit.indicatorReason,
    ];
    const text = createDescriptionText(contentData);
    description = description + title + text;
  }

  // 選考のポイント ※任意項目
  if (job.recruit?.selectionPoint) {
    const title = createDescriptionTitle('選考のポイント');
    const text = createDescriptionText(job.recruit.selectionPoint);
    description = description + title + text;
  }

  // 勤務地
  if (job.recruit?.jobState) {
    const title = createDescriptionTitle('勤務地');
    const text = createDescriptionText(job.recruit.jobState);
    description = description + title + text;
  }

  // 勤務時間
  if (job.recruit?.workTime) {
    const title = createDescriptionTitle('勤務時間');
    const text = createDescriptionText(job.recruit.workTime);
    description = description + title + text;
  }

  // 平均残業時間 ※任意項目
  const averageOvertimeHours = [
    job.recruit?.averageOvertimeHours,
    job.recruit?.averageOvertimeHoursExplanation,
  ];

  if (judgeHavingData(averageOvertimeHours)) {
    const title = createDescriptionTitle('平均残業時間');
    const content = `月${averageOvertimeHours[0]}時間${
      averageOvertimeHours[1] ?? ''
    }`;
    const text = createDescriptionText(content);
    description = description + title + text;
  }

  // 雇用形態 ※periodは任意項目
  if (job.recruit?.formDivision) {
    const title = createDescriptionTitle('雇用形態');
    const contentData = [job.recruit.formDivision, job.recruit.period];
    const text = createDescriptionText(contentData);
    description = description + title + text;
  }

  // 給与
  if (job.recruit?.salary) {
    const title = createDescriptionTitle('給与');
    const text = createDescriptionText(job.recruit.salary);
    description = description + title + text;
  }

  // 給与にプラスしてもらえる手当・インセンティブ ※任意項目 && T原稿に項目無し
  if (job.recruit?.incentive) {
    const title = createDescriptionTitle(
      '給与にプラスしてもらえる手当・インセンティブ'
    );
    const text = createDescriptionText(job.recruit.incentive);
    description = description + title + text;
  }

  // 賞与 ※任意項目 && numberOfBonusesはT原稿に項目無し
  const bonus = [job.recruit?.numberOfBonuses, job.recruit?.bonus];

  if (judgeHavingData(bonus)) {
    const title = createDescriptionTitle('賞与');
    const content = `年${bonus[0]}回${bonus[1] ?? ''}`;
    const text = createDescriptionText(content);
    description = description + title + text;
  }

  // 昇給 ※任意項目 && numberOfSalaryIncreaseはT原稿に項目無し
  const salaryIncrease = [
    job.recruit?.numberOfSalaryIncrease,
    job.recruit?.raise,
  ];

  if (judgeHavingData(salaryIncrease)) {
    const title = createDescriptionTitle('昇給');
    const content = `年${salaryIncrease[0]}回${salaryIncrease[1] ?? ''}`;
    const text = createDescriptionText(content);
    description = description + title + text;
  }

  // 入社時の想定年収 ※T原稿に項目無し
  if (job.recruit?.estimatedAnnualIncomes?.length) {
    const title = createDescriptionTitle('入社時の想定年収');
    const content: string[] = [];
    job.recruit.estimatedAnnualIncomes.forEach((data) => {
      if (!data) return {};
      content.push(`年収${data.income}万円${data.incomeExplanation ?? ''}`);
      return content;
    });
    const text = createDescriptionText(content);
    description = description + title + text;
  }

  // 社員の年収例 ※任意項目
  if (
    job.recruit &&
    job.recruit.salaryExamples &&
    job.recruit.salaryExamples.length > 0
  ) {
    const jobSummary = createDescriptionTitle('社員の年収例');
    description +=
      jobSummary + createDescriptionSalary(job.recruit.salaryExamples);
  }

  // 待遇・福利厚生 ※任意項目 && companyBenefitsはT原稿に項目無し
  const benefits = [
    job.recruit?.treatmentAndWelfare,
    job.recruit?.companyBenefits,
  ];

  if (judgeHavingData(benefits)) {
    const title = createDescriptionTitle('待遇・福利厚生');
    const text = createDescriptionText(benefits);
    description = description + title + text;
  }

  // 休日・休暇
  let holidayText = '';

  // 休日・休暇: 年間休日 ※任意項目 && T原稿に項目無し
  const holiday = [
    job.recruit?.annualHoliday,
    job.recruit?.annualHolidayExplanation,
  ];

  if (judgeHavingData(holiday)) {
    const text = `年間休日：${holiday[0]}日${holiday[1] ?? ''}`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 休日チェック ※holidayWeekExplanationは任意項目 && T原稿に項目無し
  if (job.recruit?.holidayWeek) {
    let text = '';
    // CANVAS上で「休日 チェック」が「その他（シフト／▲勤▲休など）」の場合は、job.recruit?.holidayWeekにjob.recruit?.holidayWeekExplanation（休日 補足）の値が入る。値がダブらないように分岐させる
    if (job.recruit?.holidayWeek === job.recruit?.holidayWeekExplanation) {
      text = `■${job.recruit?.holidayWeekExplanation ?? ''}`;
    } else {
      text = `■${job.recruit?.holidayWeek}<br>${
        job.recruit?.holidayWeekExplanation ?? ''
      }`;
    }
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 祝日休み ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasNationalHoliday) {
    const text = `■祝日休み<br>${
      job.recruit?.nationalHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 年末年始休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasNewYearHoliday) {
    const text = `■年末年始休暇<br>${
      job.recruit?.newYearHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: GW休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasGoldenWeekHoliday) {
    const text = `■GW休暇<br>${
      job.recruit?.goldenWeekHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 夏季休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasSummerHoliday) {
    const text = `■夏季休暇<br>${job.recruit?.summerHolidayExplanation ?? ''}`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 慶弔休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasCondolenceHoliday) {
    const text = `■慶弔休暇<br>${
      job.recruit?.condolenceHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 有給休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasPaidHolidays) {
    const text = `■有給休暇<br>${job.recruit?.paidHolidaysExplanation ?? ''}`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 産前・産後休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasMaternityHoliday) {
    const text = `■産前・産後休暇<br>${
      job.recruit?.maternityHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 育児休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasChildcareHoliday) {
    const text = `■育児休暇<br>${
      job.recruit?.childcareHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 介護休暇 ※任意項目 && T原稿に項目無し
  if (job.recruit?.hasFamilyCareHoliday) {
    const text = `■介護休暇<br>${
      job.recruit?.familyCareHolidayExplanation ?? ''
    }`;
    holidayText += createDescriptionText(text);
  }

  // 休日・休暇: 休日休暇 その他 ※任意項目
  if (job.recruit?.holiday) {
    holidayText += createDescriptionText(job.recruit?.holiday);
  }

  if (holidayText) {
    const title = createDescriptionTitle('休日・休暇');
    description = description + title + holidayText;
  }

  // 社員インタビュー
  if (
    job.interview &&
    job.interview.employeeInterviews &&
    job.interview.employeeInterviews.length > 0
  ) {
    const title = createDescriptionTitle('社員インタビュー');
    const text = createDescriptionInterview(job.interview.employeeInterviews);
    description = description + title + text;
  }
  // 取材レポート
  if (
    job.interview &&
    job.interview.reportKind &&
    (job.interview.reportTitle ||
      job.interview.reportRewarding ||
      job.interview.reportDifficult)
  ) {
    description += createDescriptionTitle('取材レポート');
    let reportKindText = '';

    switch (job.interview.reportKind) {
      case '1':
        reportKindText = '■社員の働き方は？';
        break;
      case '2':
        reportKindText = '■人や職場の雰囲気は？';
        break;
      case '3':
        reportKindText = '■会社・事業の優位性は？';
        break;
      case '4':
        reportKindText = '■商品・サービスの強みは？';
        break;
      default:
        return description;
    }

    description += `${createDescriptionText(reportKindText)}<br>`;
    if (job.interview.reportTitle)
      description += `${createDescriptionText(job.interview.reportTitle)}<br>`;
    if (job.interview.reportRewarding)
      description += `${createDescriptionText(
        job.interview.reportRewarding
      )}<br>`;
    if (job.interview.reportDifficult)
      description += `${createDescriptionText(
        job.interview.reportDifficult
      )}<br>`;
  }
  return description;
};
// ↑↑ descriotionを生成する処理 ↑↑

/**
 * 業種名を生成する処理
 * @param industry
 * @returns {string}
 */
export const createIndustryName = (industry: JobDetailResponse['industry']) => {
  let industryName = '';
  if (industry) {
    if (industry.industryLName) {
      industryName += industry.industryLName;
    }
    if (industry.industryLName && industry.industrySName) {
      industryName += COMMA_N;
    }
    if (industry.industrySName) {
      industryName += industry.industrySName;
    }
  }

  return industryName;
};

/**
 * 職種名を生成する処理
 * @param jobType
 * @returns {string}
 */
export const createOccupationalName = (
  jobType: JobDetailResponse['searchJobType']
) => {
  let jobTypeName = '';
  if (jobType) {
    if (jobType.searchJobTypeLName) {
      jobTypeName += jobType.searchJobTypeLName + COMMA_N;
    }
    if (jobType.searchJobTypeMName) {
      jobTypeName += jobType.searchJobTypeMName + COMMA_N;
    }
    if (jobType.searchJobTypeSName) {
      jobTypeName += jobType.searchJobTypeSName + COMMA_N;
    }
    if (
      jobType.searchJobTypeLName ||
      jobType.searchJobTypeMName ||
      jobType.searchJobTypeSName
    ) {
      jobTypeName = jobTypeName.slice(0, -1);
    }
  }

  return jobTypeName;
};

/**
 * 勤務時間を生成する処理
 * @param jobRecruit
 * @returns {string}
 */
export const createWorkHourText = (
  jobRecruit: JobRecruitResponse | undefined
) => {
  let workTimeText = '';
  if (jobRecruit && jobRecruit.workTime) {
    workTimeText += jobRecruit.workTime.replace(/"/g, '').replace(/\n/g, '');
    return workTimeText;
  }
  return '';
};

/**
 * 直接応募かどうかを返す
 * @param job
 * @returns {boolean}
 */
export const isDirectApply = (job: JobResponse | undefined) => {
  return job?.jobOfferKind === WJOFL_DIRECT;
};

/**
 * 待遇・福利厚生を生成する処理
 * @param jobRecruit
 * @returns {string}
 */
export const createJobBenefitsText = (
  jobRecruit: JobRecruitResponse | undefined
) => {
  let jobBenefits = '';
  if (jobRecruit?.treatmentAndWelfare) {
    jobBenefits += jobRecruit.treatmentAndWelfare
      .replace(/"/g, '')
      .replace(/\n/g, '');
  }

  if (jobRecruit?.companyBenefits) {
    jobBenefits += jobRecruit.companyBenefits
      .replace(/"/g, '')
      .replace(/\n/g, '');
  }
  return jobBenefits;
};

// APIレスポンスの年収区分の値を実際に使用する年収金額に変換する連想配列
const ANNUAL_INCOME_MASTER: { [key: number]: number } = {
  20: 2000000,
  25: 2500000,
  30: 3000000,
  35: 3500000,
  40: 4000000,
  45: 4500000,
  50: 5000000,
  55: 5500000,
  60: 6000000,
  65: 6500000,
  70: 7000000,
  80: 8000000,
  90: 9000000,
  100: 10000000,
} as const;

/**
 * 年収下限を生成する処理
 * 求人の年収下限(単位:10万円)をマスタに基づき実際の金額に変換する
 * 未設定(undefined)またはマスタ外は 0 を返す
 * @param annualIncome 年収下限のマスタ値
 * @returns 年収下限の実数値
 */
export const convertAnnualIncomeLower = (annualIncome?: number) => {
  if (annualIncome == null) {
    return 0;
  }
  return ANNUAL_INCOME_MASTER[annualIncome] ?? 0;
};

/**
 * 年収上限を生成する処理
 * 求人の年収上限(単位:10万円)をマスタに基づき実際の金額に変換する
 * 未設定(undefined)またはマスタ外または上限なし（101）は 0 を返す
 * @param annualIncome 年収上限のマスタ値
 * @returns 年収上限の実数値
 */
export const convertAnnualIncomeUpper = (annualIncome?: number) => {
  if (annualIncome == null) {
    return 0;
  }
  return ANNUAL_INCOME_MASTER[annualIncome] ?? 0;
};

/**
 * 取得した年収下限と上限に基づきbaseSalaryの構造化データの生成
 * @param annualIncomeLower
 * @param annualIncomeUpper
 * @returns baseSalary構造化データ
 */
export const createBaseSalary = (
  annualIncomeLower?: number,
  annualIncomeUpper?: number
) => {
  const minValue = convertAnnualIncomeLower(annualIncomeLower);
  const maxValue = convertAnnualIncomeUpper(annualIncomeUpper);
  if (!minValue && !maxValue) {
    return undefined;
  }
  return {
    '@type': 'MonetaryAmount',
    currency: 'JPY',
    value: {
      '@type': 'QuantitativeValue',
      ...(minValue ? { minValue } : {}),
      ...(maxValue ? { maxValue } : {}),
      unitText: 'YEAR',
    },
  };
};
